import styled, { css, keyframes } from 'styled-components';
import LogoBlank from '@images/logo-card-blank.svg';
import LogoFulfilled from '@images/logo-card-fulfilled.svg';
import ChipLines from '@images/chip-lines.svg';

const COLORS = {
  green: '#01bfa5',
  gold: '#f0e3b9',
  white: '#ffffff',
  black: '#272324',
};

const defaultTheme = css`
  background-color: #ffffff;
  background-image: url(${LogoBlank});
  border: 3px solid ${COLORS.green};
`;

const fulfilledTheme = css`
  background-color: ${({ bgColor }) => getCardColor(bgColor)};
  background-image: url(${LogoFulfilled});
  border: 3px solid ${({ bgColor }) => getCardColor(bgColor)};
`;

const getCardColor = color => (color ? `#${color}` : COLORS.black);

const selectTheme = theme => {
  return {
    default: defaultTheme,
    fulfilled: fulfilledTheme,
  }[theme];
};

const cardEntranceAnimation = keyframes`
  0% { opacity: 0; top: 80px; }
  100% { opacity: 1; top: 0; }
`;

const creditCardAnimation = css`
  animation: ${cardEntranceAnimation} 0.7s 0.3s both;
  left: 0;
  position: absolute !important;
  top: 0;
`;

const Wrapper = styled.div`
  * {
    margin: 0;
    padding: 0;
  }

  background-repeat: no-repeat;
  background-position: 175px 60px;
  background-size: 165px;
  border-radius: 15px;
  box-shadow: 0px 10px 40px #0000003b;
  font-size: 10px;
  height: 235px;
  position: relative;
  transition: background-color 0.4s ease-in-out;
  width: 354px;
  z-index: 1;

  ${({ animation }) => (animation ? creditCardAnimation : null)};
  ${({ theme }) => selectTheme(theme)};
`;

const NewCardPlaceholder = styled.span`
  display: inline-block;
  bottom: 29px;
  color: ${COLORS.green};
  font-family: 'Montserrat';
  font-size: 2.9em;
  font-weight: 600;
  left: 29px;
  opacity: ${({ hasData }) => (hasData ? 0 : 1)};
  position: absolute;
  pointer-events: none;
`;

const CardChip = styled.div`
  background-color: ${COLORS.gold};
  background-image: url(${ChipLines});
  background-repeat: no-repeat;
  background-position: center;
  background-size: 95%;
  border-radius: 3px;
  height: 30px;
  left: 28px;
  position: absolute;
  top: 64px;
  width: 43px;
`;

const CardNumber = styled.span`
  color: ${COLORS.white};
  display: inline-block;
  font-size: 2.2em;
  font-weight: 600;
  margin-bottom: 20px;
  word-spacing: 7px;
`;

const CardData = styled.div`
  bottom: 29px;
  color: ${COLORS.white};
  left: 28px;
  position: absolute;
`;

const FooterItems = styled.footer`
  display: flex;
  font-family: 'Montserrat';

  > div {
    margin-right: 20px;

    &:first-child {
      min-width: 130px;
    }
  }

  span {
    display: block;
    font-size: 1.5em;
    font-weight: 600;
    height: 10px;
    text-transform: capitalize;

    &:first-child {
      font-size: 0.8em;
      font-weight: 400;
      height: auto;
      margin-bottom: 10px;
      text-transform: uppercase;
    }
  }
`;

const CVVDots = styled.span`
  font-size: 1em !important;
`;

const flagAnimation = keyframes`
  0% { opacity: 0; }
  100% { opacity: 1; }
`;

const CardFlag = styled.figure`
  animation: ${flagAnimation} 0.3s 0.1s both;
  position: absolute;
  right: 26px;
  top: 26px;
  max-width: 70px;
`;

const IconDelete = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 0 10px 10px 10px;
  background-color: red;
  position: absolute;
  top: 0;
  right: 0;
  z-index: 1;
  display: grid;
  place-content: center;
  cursor: pointer;

  &::after,
  &::before {
    content: '';
    display: block;
    width: 15px;
    height: 2px;
    background-color: white;
  }

  &::after {
    transform-origin: center;
    transform: translateY(-1px) rotateZ(45deg);
  }

  &::before {
    transform-origin: center;
    transform: translateY(1px) rotateZ(-45deg);
  }
`;

export {
  Wrapper,
  NewCardPlaceholder,
  CardChip,
  CardData,
  CardNumber,
  FooterItems,
  CVVDots,
  CardFlag,
  IconDelete,
};
